// src/components/shared/useDokumentExtraktion.jsx
// Generischer Extraktions-Hook, unabhängig von der Dokumentklasse. Der Extraktions-Pipeline
// (POST /extract/default[/images]) ist es egal, welche Dokumentklasse dahintersteckt - was
// variiert, ist einzig der verwendete Prompt (promptKey, z.B. "Eingangsrechnung_Optimiert" für
// Rechnungen, siehe GET /prompts/{key}). Das Ergebnis (dokument/setDokument) ist bewusst
// generisch benannt - die jeweilige Dokumentklassen-Tab-Komponente destrukturiert/benennt es
// bei Bedarf lokal um, z.B.:
//   const { dokument: rechnung, setDokument: setRechnung, ... } = useDokumentExtraktion('Eingangsrechnung_Optimiert');
import { useState } from 'react'
import api from '../../api'
import { useAuth } from '../../authContext'

export const readFileAsBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result // "data:<mime>;base64,XXXX"
      resolve(result.substring(result.indexOf(',') + 1))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

export const useDokumentExtraktion = promptKey => {
  const { token, aktualisiereKontingent } = useAuth()
  const [file, setFile] = useState(null)
  const [images, setImages] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [dokument, setDokument] = useState(null)
  const [validation, setValidation] = useState(null)
  const [kontingentAufgebraucht, setKontingentAufgebraucht] = useState(false)

  const resetResult = () => {
    setError(null)
    setDokument(null)
    setValidation(null)
    setKontingentAufgebraucht(false)
  }

  const reset = () => {
    setFile(null)
    setImages(null)
    resetResult()
  }

  const selectFile = selectedFile => {
    setFile(selectedFile)
    setImages(null)
    resetResult()
  }

  const selectImages = selectedFiles => {
    setImages(selectedFiles)
    setFile(null)
    resetResult()
  }

  const extract = async () => {
    const hasImages = images && images.length > 0
    if (!file && !hasImages) {
      setError('Bitte zuerst eine Datei auswählen.')
      return
    }

    setLoading(true)
    resetResult()

    try {
      const promptResponse = await api.get(
        `/prompts/${encodeURIComponent(promptKey)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      const prompt = promptResponse.data.text

      const extractResponse = hasImages
        ? await api.post(
            '/extract/default/images',
            {
              imagesBase64: await Promise.all(images.map(readFileAsBase64)),
              prompt
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        : await api.post(
            '/extract/default',
            {
              documentBase64: await readFileAsBase64(file),
              fileExtension: '.' + file.name.split('.').pop(),
              prompt
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )

      const { finalJson, finalValidation } = extractResponse.data
      setValidation(finalValidation || null)

      if (!finalJson) {
        setError(
          finalValidation?.remarks ||
            'Die Extraktion hat kein Ergebnis geliefert.'
        )
        return
      }

      setDokument(JSON.parse(finalJson))
    } catch (err) {
      if (err.response?.status === 403) {
        // Dokumenten-Kontingent aufgebraucht (siehe CombinedRunEndpoints.KontingentAufgebrauchtResult).
        setKontingentAufgebraucht(true)
        setError(
          err.response?.data?.error ||
            'Das Dokumenten-Kontingent ist aufgebraucht.'
        )
      } else {
        const message =
          err.response?.data?.detail || err.response?.data?.title || err.message
        setError(`Fehler bei der Extraktion: ${message}`)
      }
    } finally {
      setLoading(false)
      aktualisiereKontingent()
    }
  }

  return {
    file,
    images,
    selectFile,
    selectImages,
    extract,
    loading,
    error,
    dokument,
    setDokument,
    validation,
    kontingentAufgebraucht,
    reset
  }
}

export default useDokumentExtraktion
